import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/common/components/ui/table';
import { useFormatCurrency } from '@/common/hooks/useFormatCurrency';
import { useScroll } from '@/policy/hooks/useScroll';
import { Car } from '@/policy/types/PolicyCoverage';
import { ValidationResult } from '@/policy/types/ValidationResult';
import { isMainChapter } from '@/policy/utils/cars';

import { ScrollShadows } from '../ScrollShadows/ScrollShadows';
import { ValidableSection } from '../Validation/ValidableSection';

interface DualCarsSectionProps {
  car1: Car;
  car2: Car;
  carIdentifier: string;
  validation: ValidationResult;
  onValidationChange: (validation: ValidationResult) => void;
  isSelected: boolean;
  onTableClick: (sectionUuid: string) => void;
}

export const DualCarsSection = forwardRef<HTMLDivElement, DualCarsSectionProps>(
  (
    {
      car1,
      car2,
      carIdentifier,
      validation,
      onValidationChange,
      isSelected,
      onTableClick,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { formatCurrency } = useFormatCurrency();

    const tryFormatCurrency = (value: string) => {
      if (!value) return '';
      if (value.endsWith('%') || !Number.parseFloat(value)) return value;
      return formatCurrency(value);
    };

    const {
      scrollContainerRef,
      isScrolled,
      dragProps,
      canScrollLeft,
      canScrollRight,
    } = useScroll();

    const maxSections = Math.max(car1.sections.length, car2.sections.length);

    return (
      <ValidableSection
        ref={ref}
        className="my-4"
        sectionIdentifier={carIdentifier}
        validation={validation}
        onValidationChange={onValidationChange}
        isSelected={isSelected}
        onTableClick={onTableClick}
      >
        <ScrollShadows
          canScrollLeft={canScrollLeft}
          canScrollRight={canScrollRight}
        >
          <div
            ref={scrollContainerRef}
            {...dragProps}
            className={`w-full overflow-x-auto scrollbar-visible select-none ${dragProps.className}
            [&::-webkit-scrollbar]:h-1.5
            [&::-webkit-scrollbar-track]:bg-transparent
            [&::-webkit-scrollbar-thumb]:bg-warm-gray-400
            [&::-webkit-scrollbar-thumb]:rounded-full`}
          >
            <Table
              noWrapper
              className="min-w-max w-full"
              style={{ tableLayout: 'auto' }}
            >
              <TableHeader>
                <TableRow>
                  <TableHead
                    rowSpan={2}
                    className="sticky left-0 z-20 rounded-tl-lg border-r border-warm-gray-200 min-w-[200px] align-middle font-bold text-left"
                  >
                    {t('garantie.garanties')}
                    {isScrolled && (
                      <span
                        aria-hidden
                        className="absolute top-0 bottom-0 left-full w-4 pointer-events-none bg-gradient-to-r from-black/5 to-transparent"
                      />
                    )}
                  </TableHead>
                  {/* Car 1 Headers */}
                  <TableHead
                    colSpan={3}
                    className="text-center text-background border-r border-white font-bold"
                    style={{ backgroundColor: 'var(--color-terre-400)' }}
                  >
                    {car1.carNumber} - {car1.description}
                  </TableHead>
                  {/* Car 2 Headers */}
                  <TableHead
                    colSpan={3}
                    className="rounded-tr-lg text-center text-background font-bold"
                    style={{ backgroundColor: 'var(--color-terra-cotta-400)' }}
                  >
                    {car2.carNumber} - {car2.description}
                  </TableHead>
                </TableRow>
                <TableRow>
                  {/* Car 1 Sub-headers */}
                  <TableHead
                    className="border-r border-warm-gray-200 min-w-[120px] text-background font-bold text-right"
                    style={{ backgroundColor: 'var(--color-terre-400)' }}
                  >
                    {t('garantie.montantAssurance')}
                  </TableHead>
                  <TableHead
                    className="border-r border-warm-gray-200 min-w-[92px] text-background font-bold text-right"
                    style={{ backgroundColor: 'var(--color-terre-400)' }}
                  >
                    {t('garantie.franchise')}
                  </TableHead>
                  <TableHead
                    className="border-r border-warm-gray-200 min-w-[92px] text-background font-bold text-right"
                    style={{ backgroundColor: 'var(--color-terre-400)' }}
                  >
                    {t('garantie.prime')}
                  </TableHead>
                  {/* Car 2 Sub-headers */}
                  <TableHead
                    className="border-r border-warm-gray-200 min-w-[120px] text-background font-bold text-right"
                    style={{ backgroundColor: 'var(--color-terra-cotta-400)' }}
                  >
                    {t('garantie.montantAssurance')}
                  </TableHead>
                  <TableHead
                    className="border-r border-warm-gray-200 min-w-[92px] text-background font-bold text-right"
                    style={{ backgroundColor: 'var(--color-terra-cotta-400)' }}
                  >
                    {t('garantie.franchise')}
                  </TableHead>
                  <TableHead
                    className="min-w-[92px] text-background font-bold text-right"
                    style={{ backgroundColor: 'var(--color-terra-cotta-400)' }}
                  >
                    {t('garantie.prime')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: maxSections }).map((_, index) => {
                  const section1 = car1.sections[index];
                  const section2 = car2.sections[index];
                  const warranty =
                    section1?.warranties || section2?.warranties || '';
                  const isMain = isMainChapter(warranty);

                  return (
                    <TableRow
                      key={`${car1.serialNumber}-${index}`}
                      className="bg-white even:bg-white"
                    >
                      <TableCell
                        className={`sticky left-0 z-10 bg-white text-left border-r border-warm-gray-200 ${isMain ? 'font-bold' : 'px-6'}`}
                      >
                        {warranty}
                        {isScrolled && (
                          <span
                            aria-hidden
                            className="absolute top-0 bottom-0 left-full w-4 pointer-events-none bg-gradient-to-r from-black/5 to-transparent"
                          />
                        )}
                      </TableCell>
                      {/* Car 1 Data */}
                      <TableCell className="text-right border-r border-warm-gray-200">
                        {section1
                          ? tryFormatCurrency(section1.coverageAmount)
                          : ''}
                      </TableCell>
                      <TableCell className="text-right border-r border-warm-gray-200">
                        {section1 ? section1.deductible : ''}
                      </TableCell>
                      <TableCell className="text-right border-r border-warm-gray-200">
                        {tryFormatCurrency(section1?.premium) || ''}
                      </TableCell>
                      {/* Car 2 Data */}
                      <TableCell className="text-right border-r border-warm-gray-200">
                        {section2
                          ? tryFormatCurrency(section2.coverageAmount)
                          : ''}
                      </TableCell>
                      <TableCell className="text-right border-r border-warm-gray-200">
                        {section2 ? section2.deductible : ''}
                      </TableCell>
                      <TableCell className="text-right">
                        {tryFormatCurrency(section2?.premium) || ''}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </ScrollShadows>
      </ValidableSection>
    );
  },
);

DualCarsSection.displayName = 'DualCarsSection';
